import { useState } from 'react';
import QRCode from 'qrcode';
import api from '../../utils/api';
import { errorText } from '../../utils/errorText';
import { LAYOUTS, downloadLabelPdf } from '../../utils/labelPdf';
import { printQrLabelBatch } from '../../utils/qrLabel';
import { offsetMinutes } from '../../utils/specimenCode';
import * as S from './styles';

/*
 * Pre-print: reserve a run of specimen codes for a field trip and get them as
 * a PDF sheet of QR labels (or straight to the printer).
 *
 * The codes are reserved on the server first -- POST /specimens/reserve --
 * so a printed label is always a code nobody else can be given. A label not
 * used comes back as a blank specimen that can be voided. Each label also
 * prints the specimen's short lab code (L-00042, 0054) as text; the QR stays
 * the ISO code.
 */
const MAX = 200;
const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export default function LabelBatchGenerator({ t, onReserved }) {
  const [form, setForm] = useState({ count: 24, planned_date: today(), location_code: '', layout: 'a4_3x8', caption: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [batch, setBatch] = useState(null);
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  // The reserved specimens' lab codes, by ISO code.
  const labCodes = (b) => new Map((b.specimens || []).map((sp) => [sp.specimen_code, sp.lab_code]));
  const labels = (b) => {
    const lab = labCodes(b);
    return b.codes.map((code) => ({
      code, ...(lab.get(code) ? { lab_code: lab.get(code) } : {}), caption: form.caption.trim() || undefined,
    }));
  };
  const fileName = (b) => `specimen-labels-${b.prefix}.pdf`;

  const reserve = async (e) => {
    e.preventDefault();
    setError('');
    const count = Math.min(MAX, Math.max(1, Number(form.count) || 1));
    setBusy(true);
    try {
      const res = await api.post('/specimens/reserve', {
        count, planned_date: form.planned_date || undefined,
        location_code: form.location_code.trim() || undefined, utc_offset_minutes: offsetMinutes(),
      });
      setBatch(res.data);
      downloadLabelPdf(labels(res.data), fileName(res.data), { layout: form.layout, title: res.data.prefix });
      onReserved?.(res.data);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const print = async () => {
    const lab = labCodes(batch);
    const items = await Promise.all(batch.codes.map((code) =>
      QRCode.toDataURL(code, { width: 220, margin: 1 })
        .then((dataUrl) => ({ dataUrl, text: lab.get(code) ? `${code} · ${lab.get(code)}` : code }))));
    if (!printQrLabelBatch(items)) setError(t('labels.popupBlocked'));
  };

  return (
    <form onSubmit={reserve} style={S.card} data-testid="label-batch" aria-labelledby="labels-title">
      <h3 id="labels-title" style={S.sectionTitle}>{t('labels.title')}</h3>
      <p style={S.hint}>{t('labels.intro')}</p>
      <div style={S.row}>
        <label style={{ ...S.label, flex: '0 1 110px' }}>
          {t('labels.count')}
          <input type="number" min="1" max={MAX} value={form.count} onChange={set('count')} style={S.input}
            title={t('labels.tip.count')} data-testid="labels-count" />
        </label>
        <label style={{ ...S.label, flex: '1 1 160px' }}>
          {t('labels.plannedDate')}
          <input type="date" value={form.planned_date} onChange={set('planned_date')} style={S.input}
            title={t('labels.tip.plannedDate')} data-testid="labels-date" />
        </label>
        <label style={{ ...S.label, flex: '1 1 140px' }}>
          {t('labels.site')}
          <input value={form.location_code} onChange={set('location_code')} maxLength={12} dir="ltr"
            placeholder="GOLAN" style={{ ...S.input, ...S.mono, textTransform: 'uppercase' }}
            title={t('labels.tip.site')} data-testid="labels-site" />
        </label>
        <label style={{ ...S.label, flex: '1 1 160px' }}>
          {t('labels.layout')}
          <select value={form.layout} onChange={set('layout')} style={S.input} title={t('labels.tip.layout')}
            data-testid="labels-layout">
            {Object.keys(LAYOUTS).map((key) => <option key={key} value={key}>{t(`labels.layouts.${key}`)}</option>)}
          </select>
        </label>
        <label style={{ ...S.label, flex: '2 1 200px' }}>
          {t('labels.caption')}
          <input value={form.caption} onChange={set('caption')} maxLength={40} dir="ltr" style={S.input}
            title={t('labels.tip.caption')} data-testid="labels-caption" />
        </label>
      </div>
      <p style={S.hint}>{t('labels.siteHint')}</p>
      {error && <p style={S.error} role="alert">{error}</p>}
      <div style={{ ...S.row, marginTop: 8 }}>
        <button type="submit" disabled={busy} style={S.btn()} title={t('labels.tip.reserve')} data-testid="labels-reserve">
          {busy ? t('labels.reserving') : t('labels.reserve')}
        </button>
      </div>
      {batch && (
        <div style={{ marginTop: 12 }} data-testid="labels-result" role="status">
          <p style={{ margin: '0 0 6px', fontSize: 14 }}>
            {t('labels.reserved', { count: batch.count })}{' '}
            <span dir="ltr" style={S.mono}>{batch.codes[0]} … {batch.codes[batch.codes.length - 1]}</span>
          </p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" style={S.ghostBtn} title={t('labels.tip.download')} data-testid="labels-download"
              onClick={() => downloadLabelPdf(labels(batch), fileName(batch), { layout: form.layout, title: batch.prefix })}>
              {t('labels.download')}
            </button>
            <button type="button" style={S.ghostBtn} onClick={print} title={t('labels.tip.print')} data-testid="labels-print">
              {t('labels.print')}
            </button>
          </div>
        </div>
      )}
    </form>
  );
}
